import { v4 as uuid } from 'uuid';

import { DataFieldType } from '~/enums/DataFieldType';
import { EntityType } from '~/enums/EntityType';
import { Filter } from '~/evifields/src/types/Filter';
import {
  SearchFilterRecord,
  SearchQuery,
} from '~/features/advanced-search/types';
import { Highlight } from '~/features/document-viewer/types/highlight';
import {
  FieldClassificationValueBase,
  FieldExtractionValueBase,
  FieldModel,
  FieldModelConfigState,
  FieldValue,
  FieldValueBase,
} from '~/features/x-ray/fields/types';
import {
  mapDataFieldTypeToOutputType,
  mapOutputTypeToDataFieldType,
} from '~/features/x-ray/fields/utils';
import {
  LibraryModelVersion,
  SampleOutputDashboard,
  Tag,
} from '~/features/x-ray/library/types';
import { PromptModelTestCase } from '~/features/x-ray/types/deprecated';
import {
  TargetEntity,
  TargetEntityDetails,
} from '~/features/x-ray/types/entity';
import { ModelVersion } from '~/features/x-ray/types/model';
import {
  OptimizeResult,
  OptimizeResults,
  OptimizeTestCase,
} from '~/features/x-ray/types/optimize';
import {
  TableColumnSpec,
  TableColumnSpecState,
  TableValue,
} from '~/features/x-ray/types/table';
import { Nullable } from '~/types/Nullable';
import { EntityQuery, FilterQueryEntity } from '~/types/OpenSearch';
import { PilotId } from '~/types/PilotId';
import { Uuid } from '~/types/Uuid';

import { DocumentHighlightSource } from '../../types/sylvanus/conversation';
import {
  ApiLibraryModelVersion,
  FieldTargetInformation,
  LibraryTag,
} from '../../types/sylvanus/library';
import {
  ClassificationOption,
  OptimizeResultsForConfig,
  OptimizeRun,
  OptimizeState,
  PromptModelConfig,
  PromptModelPublishStatus,
  PromptModelTestCase as PromptModelTestCaseResponse,
  PromptModelVersion,
  SearchScope,
  OptimizeTestCase as ServerOptimizeTestCase,
  TableValue as ServerTableValue,
  TableCell,
} from '../../types/sylvanus/model';
import { toSearchV3Filter } from '../searchV3';

const mapSourceToHighlight = (source: DocumentHighlightSource): Highlight => {
  const { coordinates: sourceCoordinates = [], html_tokens = [] } = source;

  return {
    id: uuid(),
    data: source,
    highlighter: 'ai',
    htmlTokens: html_tokens ?? [],
    pdfCoordinates: sourceCoordinates?.[0]?.coordinates,
    ...(source.text ? { text: source.text } : {}),
  };
};

const mapFieldValueSourcesToHighlights = (
  value: FieldValueBase,
  highlightMethod: string,
): Highlight[] | undefined => {
  const highlights: Highlight[] = [];

  if (!value.sources) {
    return undefined;
  }

  value.sources.forEach((source) => {
    switch (highlightMethod) {
      case 'context_first':
      default:
        if (source.context) {
          highlights.push(mapSourceToHighlight(source.context));
        } else if (source.value) {
          highlights.push(mapSourceToHighlight(source.value));
        }
        break;
      case 'context_only':
        if (source.context) {
          highlights.push(mapSourceToHighlight(source.context));
        }
        break;
      case 'value_first':
        if (source.value) {
          highlights.push(mapSourceToHighlight(source.value));
        } else if (source.context) {
          highlights.push(mapSourceToHighlight(source.context));
        }
        break;
      case 'value_only':
        if (source.value) {
          highlights.push(mapSourceToHighlight(source.value));
        }
        break;
    }
  });

  return highlights;
};

const mapTableCellSourceToHighlights = (
  value: TableCell,
): Highlight[] | undefined => {
  if (!value.source) {
    return undefined;
  }

  return [
    {
      id: uuid(),
      data: value.source,
      highlighter: 'ai',
      htmlTokens: value.source.html_tokens ?? [],
      pdfCoordinates: value.source.coordinates?.[0]?.coordinates,
      ...(value.source.text ? { text: value.source.text } : {}),
    },
  ];
};

export const mapValue = (
  value: Nullable<FieldValueBase>,
  highlightMethod: string,
): Nullable<FieldValue> => {
  if (!value) {
    return null;
  }
  const mappedValue = {
    highlights: mapFieldValueSourcesToHighlights(value, highlightMethod),
    type: value.value_type,
  };
  // split the value into classification and extraction
  // to prevent TS from complaining about the type
  if (value.value_type === 'classification') {
    return {
      ...mappedValue,
      value: value.value as FieldClassificationValueBase['value'],
    } as FieldValue;
  }
  return {
    ...mappedValue,
    value: value.value as FieldExtractionValueBase['value'],
  };
};

export const mapTableValue = (
  tableValue: Nullable<ServerTableValue>,
): Nullable<TableValue> => {
  if (!tableValue || tableValue.value_type !== 'table_value') return null;
  return {
    spec: tableValue.spec,
    value: tableValue.value.map((tableRow, index) => ({
      id: `${index + 1}`,
      cells: tableRow.cells.map((tableCell) => ({
        id: tableCell.cell_id ?? undefined,
        highlights: mapTableCellSourceToHighlights(tableCell),
        type: tableCell.value_type,
        value: tableCell.value,
      })),
    })),
  };
};

export const mapTestCase = (
  response: PromptModelTestCaseResponse,
  highlightMethod: string,
): PromptModelTestCase => {
  return {
    entity: response.entity,
    feedback: response.user_feedback ?? null,
    goldValue: mapValue(
      (response.gold_value as FieldValueBase) ?? null,
      highlightMethod,
    ),
    llmOutput: response.llm_output ? JSON.stringify(response.llm_output) : null,
    modelId: response.prompt_model_id,
    modelValue: mapValue(
      (response.promptmodel_value as FieldValueBase) ?? null,
      highlightMethod,
    ),
    number: response.number,
    outcome: response.outcome ?? 'unknown',
    state: response.state ?? 'not_started',
    tableValue: mapTableValue(response.promptmodel_value as ServerTableValue),
    versionNumber: response.prompt_model_version_number,
  };
};

export const mapDataFieldType = (config: PromptModelConfig): DataFieldType => {
  switch (config.prompt_model_type) {
    case 'field_classification':
      return config.is_multi
        ? DataFieldType.ARRAY_MULTIPLE
        : DataFieldType.ARRAY_SINGLE;
    case 'field_extraction':
    default:
      switch (config.output_type) {
        case 'datetime':
          return DataFieldType.DATE;
        case 'float':
        case 'int':
          return DataFieldType.NUMBER;
        case 'text_area':
          return DataFieldType.TEXT_AREA;
        case 'string':
        default:
          return DataFieldType.STRING;
      }
  }
};

export const mapFieldClassificationConfigAttributes = (
  config: FieldModelConfigState,
): Partial<{
  allow_additional_options: boolean;
  default_option: Nullable<ClassificationOption>;
  is_multi: boolean;
  options: ClassificationOption[];
}> => {
  const { fieldClassification } = config;
  if (fieldClassification) {
    return {
      allow_additional_options: fieldClassification.isOpenEnded,
      default_option: fieldClassification.defaultOption,
      is_multi: fieldClassification.isMulti,
      options: fieldClassification.options,
    };
  }
  return {};
};

export const mapLibraryModelVersion = (
  version: ApiLibraryModelVersion,
): LibraryModelVersion => ({
  changelog: version.changelog_entry,
  config: version.configuration,
  //l10n-ignore: API parsing no formatting required
  createdDate: new Date(version.datetime_created),
  description: version.description,
  label: version.name,
  latest: version.latest,
  libraryModelId: version.library_model_id,
  promptModelId: version.linked_models?.length
    ? version.linked_models[0].prompt_model_id
    : null,
  promptModelPublishStatus: version.linked_models?.length
    ? mapPublishStatus(version.linked_models[0].publish_status)
    : null,
  sampleOutputs: version.sample_outputs
    ? {
        text: version.sample_outputs.text,
        dashboard: version.sample_outputs.dashboard as SampleOutputDashboard,
      }
    : undefined,
  tags: version.tags ? mapLibraryModelVersionTags(version.tags) : [],
  targetEntityDetails: mapLibraryModelVersionTarget(
    version.target as FieldTargetInformation,
  ),
  version: version.version,
});

const mapPublishStatus = (
  status: Nullable<PromptModelPublishStatus>,
): LibraryModelVersion['promptModelPublishStatus'] => {
  switch (status) {
    case 'completed':
    case 'resumed':
      return 'published';
    case 'failed':
      return 'error';
    case 'in_progress':
      return 'in_progress';
    case 'suspended':
      return 'suspended';
    case null:
      return 'draft';
    default:
      return null;
  }
};

const mapLibraryModelVersionTags = (tags: LibraryTag[]): Tag[] => {
  return tags.map((tag) => ({
    type: tag.tag_type,
    label: tag.tag_value,
  }));
};

const mapLibraryModelVersionTarget = (
  target: FieldTargetInformation,
): TargetEntityDetails => ({
  id: '-',
  label: mapValueTypeToEntityLabel(target.value_type),
  type: (target.entity_type ?? EntityType.Field) as TargetEntity['type'],
});

const mapValueTypeToEntityLabel = (
  type: FieldTargetInformation['value_type'],
) => {
  switch (type) {
    case 'string':
      return 'Text';
    case 'text_area':
      return 'Text area';
    case 'int':
    case 'float':
      return 'Number';
    case 'datetime':
      return 'Date';
    case 'classification':
      return 'Dropdown';
    default:
      return 'Unknown';
  }
};

export const mapModelConfigAttributes = (
  config: FieldModelConfigState,
): {
  output_type: PromptModelConfig['output_type'];
  prompt_model_type: PromptModelConfig['prompt_model_type'];
} => {
  const { field, fieldClassification } = config;

  let outputType: PromptModelConfig['output_type'] = 'string';
  switch (field?.type) {
    case DataFieldType.DATE:
      outputType = 'datetime' as const;
      break;
    case DataFieldType.NUMBER:
      outputType = 'float' as const;
      break;
    case DataFieldType.TEXT_AREA:
      outputType = 'text_area' as const;
      break;
    case DataFieldType.STRING:
    default:
      outputType = 'string' as const;
      break;
  }

  return {
    output_type: outputType,
    prompt_model_type: fieldClassification
      ? 'field_classification'
      : 'field_extraction',
  };
};

export const mapModelVersionToFieldModel = ({
  modelId,
  version,
  fieldId,
  highlightMethod,
  optimizeState,
  publishStatus,
}: {
  modelId: Uuid;
  version: PromptModelVersion;
  fieldId?: PilotId;
  highlightMethod: string;
  optimizeState: Nullable<OptimizeState>;
  publishStatus: Nullable<PromptModelPublishStatus>;
}): FieldModel => {
  const testCases: PromptModelTestCase[] =
    Object.values(version.test_cases ?? {}).map((testCase) =>
      mapTestCase(testCase, highlightMethod),
    ) ?? [];

  return {
    config: {
      field: {
        // @ts-ignore -- TODO: decouple field-specific logic from the model config
        id: fieldId,
        label: version.config.field_name,
        type: mapDataFieldType(version.config),
      },
      // @ts-ignore -- TODO: decouple query-specific logic from the model config
      query: version.config.scope?.data?.attributes.query,
      instructions: version.config.instructions ?? '',
      fieldClassification:
        version.config.prompt_model_type === 'field_classification'
          ? {
              isMulti: version.config.is_multi ?? false,
              options: version.config.options ?? [],
              defaultOption: version.config.default_option ?? null,
              isOpenEnded: version.config.allow_additional_options ?? true, // sylvanus defaults to open-ended
            }
          : undefined,
      internal: version.config.internal,
      stringMatchingLevel:
        version.config.prompt_model_type === 'field_extraction'
          ? version.config.string_matching_level
          : undefined,
    },
    id: modelId,
    libraryModelId: version.library_model_info?.library_model_id,
    libraryModelVersion: version.library_model_info?.library_model_version,
    name: version.config.field_name,
    modifiedBy: parseInt(version.created_by_user_id) as PilotId,
    //l10n-ignore: API parsing no formatting required
    modifiedDate: new Date(version.datetime_created),
    optimizeState,
    publishStatus,
    testCases,
    version: version.version_number,
  };
};

export const mapPromptModelVersionToTableModelVersion = ({
  modelId,
  version,
  highlightMethod,
  publishStatus,
}: {
  modelId: Uuid;
  version: PromptModelVersion;
  highlightMethod: string;
  publishStatus: Nullable<PromptModelPublishStatus>;
}): ModelVersion => {
  return {
    config: version.config,
    modelId,
    libraryModelId: version.library_model_info?.library_model_id,
    libraryModelVersion: version.library_model_info?.library_model_version,
    createdBy: parseInt(version.created_by_user_id) as PilotId,
    //l10n-ignore: API parsing no formatting required
    createdDate: new Date(version.datetime_created),
    optimize: version.optimize
      ? {
          state: version.optimize.state,
          version: version.optimize.version_number,
        }
      : null,
    publishStatus,
    scope: version.config.scope
      ? (version.config.scope.data.attributes.query as SearchQuery)
      : [],
    testCases: Object.values(version.test_cases ?? []).map((testCase) =>
      mapTestCase(testCase, highlightMethod),
    ),
    version: version.version_number,
  };
};

const mapOptimizeTestCase = (
  testCase: ServerOptimizeTestCase,
  originalTestCase: ServerOptimizeTestCase,
  highlightMethod: string,
): OptimizeTestCase => ({
  ...testCase,
  modelValue: mapValue(
    (testCase.promptmodel_value as FieldValueBase) ?? null,
    highlightMethod,
  ),
  isNotChanged: testCase.outcome === originalTestCase.outcome,
});

const mapOptimizeOption = (
  result: OptimizeResultsForConfig,
  originalResult: OptimizeResultsForConfig,
  isRecommended: boolean,
  highlightMethod: string,
): OptimizeResult => {
  const testCases = Object.values(result.test_cases).map((testCase) =>
    mapOptimizeTestCase(
      testCase,
      originalResult.test_cases[testCase.number],
      highlightMethod,
    ),
  );
  return {
    config: result.config,
    isRecommended,
    noUserConfigChanges: result.no_user_config_changes,
    stats: result.stats,
    tag: result.tag,
    testCases,
  };
};

/**
 * Maps the results of an OptimizeRun to an array of `OptimizeResult`s and sorts them
 */
const mapResults = (
  { original_results, results }: OptimizeRun,
  highlightMethod: string,
): OptimizeResult[] => {
  return results!
    .map((result) => {
      const isRecommended =
        result.tag === 'best_overall' ||
        (results!.length === 1 && result.tag === 'best_no_user_config_changes');
      return mapOptimizeOption(
        result,
        original_results,
        isRecommended,
        highlightMethod,
      );
    })
    .sort((a: OptimizeResult, b: OptimizeResult) => {
      const order = [
        'best_no_user_config_changes',
        'best_overall',
        'best_strictly_better',
      ];
      return order.indexOf(a.tag) - order.indexOf(b.tag);
    });
};

export const mapOptimizeResults = (
  response: OptimizeRun,
  highlightMethod: string,
): OptimizeResults => ({
  state: response.state,
  progress: response.progress ?? null,
  results: response.results ? mapResults(response, highlightMethod) : null,
});

export const mapScopeToFilters = (
  scope: SearchScope,
  searchFilters: SearchFilterRecord,
): Filter[] => {
  return (scope.data.attributes.query as EntityQuery[])
    .filter(
      (filter: EntityQuery): filter is FilterQueryEntity =>
        filter.type === 'filter',
    )
    .map((filter: FilterQueryEntity) =>
      toSearchV3Filter(filter, searchFilters),
    );
};

export const mapTableColumnSpecState = (
  column: TableColumnSpecState,
): TableColumnSpec => ({
  description: column.description,
  column_type:
    column.columnType && mapDataFieldTypeToOutputType(column.columnType),
  name: column.name,
});

export const mapTableColumnConfig = (
  column: TableColumnSpec,
): TableColumnSpecState => ({
  description: column.description ?? '',
  columnType: mapOutputTypeToDataFieldType(column.column_type),
  name: column.name,
});
